const getMatchedScenarios = () => {
    const { components = [], scenarios = [] } = STATE.data || {};

    const active = {};
    components.forEach(c => { active[Number(c.id)] = STATE.selected[Number(c.id)] ?? -1; });

    const matchComponentRule = (rule) => {
        const cid = Number(rule.component_id);
        const operator = String(rule.operator || 'in');
        const list = Array.isArray(rule.products) ? rule.products.map(Number) : [];
        const val = active[cid];

        if (list.length === 0) {
            return val === -1;
        }

        if (operator === 'not-in') {
            return !list.includes(val);
        }
        return list.includes(val);
    };

    const matchOptionRules = (rules) => {
        if (!Array.isArray(rules) || !rules.length) return true;

        return rules.every(r => {
            const cid = Number(r.component_id);
            const operator = String(r.operator || 'in');
            const list = Array.isArray(r.products) ? r.products.map(Number) : [];
            const selectedPid = active[cid];
            if (selectedPid === -1) return false;

            if (operator === 'not-in') return !list.includes(selectedPid);
            return list.includes(selectedPid);
        });
    };

    const matched = (scenarios || []).filter(sc => {
        const compRules = sc?.conditions?.component_rules || [];
        const optRules  = sc?.conditions?.option_rules || [];

        const compOk = compRules.every(matchComponentRule);
        if (!compOk) return false;

        return true;
    });

    if (matched && matched.length) {
        LAST_MATCHED_SCENARIOS = matched;
        return matched;
    }
    return LAST_MATCHED_SCENARIOS || [];
};

const buildAllowedMapForScenario = (baseMap) => (sc) => {
    const map = new Map();
    baseMap.forEach((set, cid) => map.set(cid, new Set(set)));

    if (Array.isArray(sc.available_components) && sc.available_components.length) {
        const mentioned = new Set(sc.available_components.map(ac => Number(ac.component_id)));
        mentioned.forEach(cid => map.set(cid, new Set())); // reset to empty
        sc.available_components.forEach(ac => {
            const cid = Number(ac.component_id);
            const pid = Number(ac.product_id);
            if (!map.has(cid)) map.set(cid, new Set());
            map.get(cid).add(pid);
        });
        return map;
    }

    const actions = sc.scenario_actions || {};
    const cc = actions.conditional_components || { is_active: 'no', hidden_components: [] };
    const co = actions.conditional_options    || { is_active: 'no', modifier: {}, component_data: {} };

    if (cc.is_active === 'yes') {
        (cc.hidden_components || []).map(Number).forEach(cid => map.set(cid, new Set()));
    }

    if (co.is_active === 'yes') {
        const modifiers = co.modifier || {};    // { cid: 'in' | 'not-in' }
        const data      = co.component_data || {}; // { cid: [pid,...] }

        Object.keys(modifiers).forEach(cidStr => {
            const cid  = Number(cidStr);
            const mode = String(modifiers[cidStr]);
            const list = (data[cidStr] || []).map(Number);

            if (!map.has(cid)) map.set(cid, new Set());
            const set = map.get(cid);

            if (mode === 'in') {
                map.set(cid, new Set(list));
            } else if (mode === 'not-in') {
                list.forEach(pid => set.delete(pid));
            }
        });
    }

    return map;
};

let LAST_MATCHED_SCENARIOS = [];

function getStableMatchedScenarios() {
    const current = getMatchedScenarios();
    if (current && current.length) {
        LAST_MATCHED_SCENARIOS = current;
        return current;
    }
    return LAST_MATCHED_SCENARIOS || [];
}

// Components the last computeAllowedByComponent() call hid outright via scenario actions.
let LAST_HIDDEN_COMPONENTS = new Set();

// A component hidden by scenarios has no allowed products whatever its own product list is,
// so narrowing it or fetching its variations cannot change anything.
function isComponentHiddenByScenarios(componentId) {
    computeAllowedByComponent();
    return LAST_HIDDEN_COMPONENTS.has(Number(componentId));
}

function computeAllowedByComponent() {
    const data = STATE.data || {};
    const components = data.components || [];

    const base = new Map();
    components.forEach(c => {
        const cid = Number(c.id);
        const set = new Set((c.products || []).map(p => Number(p.product_id)));
        base.set(cid, set);
    });

    LAST_HIDDEN_COMPONENTS = new Set();
    const matched = getMatchedScenarios();
    if (!matched.length) return base;

    const whitelistsByCid = new Map();
    const blacklistByCid  = new Map();
    const hideComp        = new Set();
    LAST_HIDDEN_COMPONENTS = hideComp;

    const scenarioEffectsByComponent = new Map();
    // Effects only feed dev-mode logs (admins); building them on every call cost ~150 ms per step for visitors.
    const trackEffects = !!window.WCCP_DEV_MODE;
    const pushEffect = (cid, sc, effect, products) => {
        if (!trackEffects) return;
        cid = Number(cid);
        if (!scenarioEffectsByComponent.has(cid)) {
            scenarioEffectsByComponent.set(cid, []);
        }
        scenarioEffectsByComponent.get(cid).push({
            scenarioId: sc.id || sc.scenario_id || sc.slug || sc.name || '(no-id)',
            effect,
            products: Array.isArray(products)
                ? products.map(Number)
                : (products instanceof Set ? [...products].map(Number) : null)
        });
    };

    const addWL = (cid, set) => {
        cid = Number(cid);
        if (!whitelistsByCid.has(cid)) whitelistsByCid.set(cid, []);
        // Sets are only read (wl.has) below, so they need no copy; arrays (show_only_options) still become Sets.
        whitelistsByCid.get(cid).push(set instanceof Set ? set : new Set(set));
    };
    const addBL = (cid, arr) => {
        cid = Number(cid);
        if (!blacklistByCid.has(cid)) blacklistByCid.set(cid, new Set());
        arr.map(Number).forEach(pid => blacklistByCid.get(cid).add(pid));
    };

    const ensureArray = (x) => Array.isArray(x) ? x : [];

    matched.forEach(sc => {
        if (Array.isArray(sc.available_components) && sc.available_components.length) {
            // Scenario data never changes after normalize(), so group its thousands of entries once per scenario
            // instead of on every call (this function runs once per remaining step on each click: ~0.7 s on Hinges).
            if (!sc.__allowedByCid) {
                sc.__allowedByCid = new Map();
                sc.available_components.forEach(ac => {
                    const cid = Number(ac.component_id);
                    const pid = Number(ac.product_id);
                    if (!sc.__allowedByCid.has(cid)) sc.__allowedByCid.set(cid, new Set());
                    sc.__allowedByCid.get(cid).add(pid);
                });
            }
            sc.__allowedByCid.forEach((set, cid) => {
                addWL(cid, set);
                pushEffect(cid, sc, 'available_components', set);
            });
        }

        const newActions = sc?.conditions?.actions || {};

        ensureArray(newActions.hide_components)
            .map(Number)
            .forEach(cid => {
                hideComp.add(cid);
                pushEffect(cid, sc, 'conditions.hide_components', null);
            });

        const newHideOpts = newActions.hide_options || {};
        const newShowOnly = newActions.show_only_options || {};

        Object.keys(newHideOpts).forEach(cidStr => {
            const cid = Number(cidStr);
            const list = ensureArray(newHideOpts[cidStr]).map(Number);
            if (list.length) {
                addBL(cid, list);
                pushEffect(cid, sc, 'conditions.hide_options', list);
            }
        });

        Object.keys(newShowOnly).forEach(cidStr => {
            const cid = Number(cidStr);
            const list = ensureArray(newShowOnly[cidStr]).map(Number);
            if (list.length) {
                addWL(cid, list);
                pushEffect(cid, sc, 'conditions.show_only_options', list);
            }
        });

        const legacy = sc.scenario_actions || {};
        const cc = legacy.conditional_components || { is_active: 'no', hidden_components: [] };
        const co = legacy.conditional_options || { is_active: 'no', modifier: {}, component_data: {} };

        if (cc.is_active === 'yes') {
            ensureArray(cc.hidden_components)
                .map(Number)
                .forEach(cid => {
                    hideComp.add(cid);
                    pushEffect(cid, sc, 'legacy.conditional_components.hidden_components', null);
                });
        }

        if (co.is_active === 'yes') {
            const mod = co.modifier || {};
            const dat = co.component_data || {};
            Object.keys(mod).forEach(cidStr => {
                const cid = Number(cidStr);
                const mode = String(mod[cidStr]);
                const list = ensureArray(dat[cidStr]).map(Number);
                if (!list.length) return;

                if (mode === 'in') {
                    addWL(cid, new Set(list));
                    pushEffect(cid, sc, 'legacy.conditional_options.in', list);
                } else if (mode === 'not-in') {
                    addBL(cid, list);
                    pushEffect(cid, sc, 'legacy.conditional_options.not-in', list);
                }
            });
        }
    });

    const result = new Map();

    base.forEach((baseSet, cid) => {
        if (hideComp.has(cid)) {
            result.set(cid, new Set());
            return;
        }

        let allowed = new Set([...baseSet]);

        const whs = whitelistsByCid.get(cid) || [];
        if (whs.length) {
            allowed = new Set([...baseSet]);
            whs.forEach(wl => {
                allowed = new Set([...allowed].filter(pid => wl.has(pid)));
            });
        }

        const bl = blacklistByCid.get(cid) || new Set();
        if (bl.size) {
            allowed = new Set([...allowed].filter(pid => !bl.has(pid)));
        }

        result.set(cid, allowed);

        // Debug log per component allowed products (IF enabled in dev mode)
        // if (window.WCCP_DEV_MODE?.debug?.allowedComponents) {
        //     devLog('ALLOWED_COMPONENT', {
        //         // step: stepEl ? Number(stepEl.dataset.step) : null,
        //         component_id: cid,
        //         base_products: window.WCCP_DEBUG?.baseProductsByComponent?.[cid] || [],
        //         allowed_products: [...allowed],
        //         // effects: scenarioEffectsByComponent.get(cid) || []
        //         // effects: window.WCCP_DEBUG?.scenarioEffectsByComponent?.[cid] || []
        //     });
        // }
    });

    window.WCCP_DEBUG = window.WCCP_DEBUG || {};
    window.WCCP_DEBUG.baseProductsByComponent = Object.fromEntries(
        [...base.entries()].map(([cid, set]) => [cid, [...set]])
    );
    window.WCCP_DEBUG.scenarioEffectsByComponent = Object.fromEntries(
        [...scenarioEffectsByComponent.entries()]
    );

    return result;
}
